import { type WorkflowStateView } from "shared/mappers/database/workflow/workflow-state";
import { WorkflowStateType } from "shared/workflow/workflow-state";
import { WorkflowWaitStateEvents } from "shared/workflow/workflow-wait-state-events";

/**
 * The event an admin sends is the event the target state is *entered with* -
 * `setState` jumps the runner straight to the state by name, so no transition
 * is ever traversed to get there. What the event does next depends entirely on
 * the state type:
 *
 * - WAIT states compare it against their `nextState.event`. A match rolls the
 *   instance straight through that transition; anything else parks it there.
 * - FUNCTION states ignore it when deciding where to go - the workflow function
 *   picks its own exit (usually from an invokeParam, the way the certification
 *   function reads `certificationEvent`). The event is only recorded in state
 *   history, which conditional-state-history states can later match on.
 *
 * So the useful option list is the outbound `nextState` for WAIT states - the
 * one event that actually moves them - and for everything else the events that
 * lead *into* the state, which the database derives per state as `entryEvent`.
 * Never a state's own outbound `transitions`, which its function picks between.
 *
 * Conditional states are out of scope: `entryEvent` is not derived through
 * them, so a state reachable only from one is offered the generics alone.
 */
export type WorkflowStateEventOption = {
  event: string;
  label: string;
  /** Offered on every state rather than read off this state's definition. */
  isGeneric?: boolean;
  /**
   * Whether posting this event moves the instance off the state. Only a WAIT
   * state's own `nextState.event` does, so this is false everywhere else -
   * including on FUNCTION states, which move because their function says so
   * rather than because of the event they were handed.
   */
  advances: boolean;
};

/**
 * Offered on every state, so an admin can post the events that drive WAIT
 * states anywhere. A state that already names one of them as a real entry
 * event keeps its own wording and the generic is dropped, so an event never
 * appears in the list twice.
 */
const GENERIC_EVENTS: string[] = Object.values(WorkflowWaitStateEvents);

/**
 * The runner compares WAIT events case-insensitively, so the list has to as
 * well - otherwise a definition naming `submit` would show both its own entry
 * and `Generic - Submit`, and only one of them would be marked as advancing.
 */
const isSameEvent = (a: string, b: string) =>
  a.toLowerCase() === b.toLowerCase();

/**
 * The `nextState` a WAIT state advances on, or undefined for any other state
 * type and for a terminal WAIT state, which has nowhere to go.
 *
 * Terminal is not always spelled as a missing key: 11 WAIT states in the
 * 2026-09 production dump are stored as `nextState: {}`, so an object with no
 * `event` counts as nowhere to go too - the runner compares `nextState.event`
 * and would never match one.
 */
const getWaitTransition = (selectedState: WorkflowStateView) => {
  if (selectedState.type !== WorkflowStateType.WAIT) {
    return undefined;
  }

  const nextState =
    "nextState" in selectedState ? selectedState.nextState : undefined;

  return nextState?.event ? nextState : undefined;
};

/**
 * A WAIT state advances only when the posted event matches its own
 * `nextState.event`, so that is the one worth offering there. Every other state
 * type ignores the event for routing, so what matters is the events that lead
 * into it - already on the row as `entryEvent`, no second fetch needed.
 */
const getDefinitionEventOptions = (
  selectedState: WorkflowStateView,
): WorkflowStateEventOption[] => {
  if (selectedState.type === WorkflowStateType.WAIT) {
    const nextState = getWaitTransition(selectedState);

    return nextState
      ? [
          {
            event: nextState.event,
            label: `${nextState.event} (advances to ${nextState.next})`,
            advances: true,
          },
        ]
      : [];
  }

  return (selectedState.entryEvent ?? []).map((entryEvent) => ({
    event: entryEvent.event,
    label: `${entryEvent.event} (entered from ${entryEvent.fromStateName})`,
    advances: false,
  }));
};

/**
 * The generics first, then the events the definition says lead here. A state
 * with nothing pointing at it - an initial state, or one an admin only ever
 * jumps to by hand - comes back with only the generics, which is fine: the
 * select still takes a typed custom event.
 *
 * On a WAIT state the generics are spelled out as non-advancing, because that
 * is the one state type where the posted event decides whether the instance
 * moves and the difference is worth seeing before saving.
 */
export const buildWorkflowStateEventOptions = ({
  selectedState,
}: {
  selectedState?: WorkflowStateView | null;
}): WorkflowStateEventOption[] => {
  if (!selectedState) {
    return [];
  }

  const definitionOptions = getDefinitionEventOptions(selectedState);
  const isWaitState = selectedState.type === WorkflowStateType.WAIT;

  const genericOptions = GENERIC_EVENTS.filter(
    (event) =>
      !definitionOptions.some((option) => isSameEvent(option.event, event)),
  ).map((event) => ({
    event,
    label: isWaitState
      ? `Generic - ${event} (does not advance)`
      : `Generic - ${event}`,
    isGeneric: true,
    advances: false,
  }));

  return [...genericOptions, ...definitionOptions];
};

/**
 * Every case here still submits fine - the event just lands in state history
 * instead of moving the instance - so this warns rather than blocks.
 */
export const getWorkflowStateEventWarning = ({
  eventName,
  options,
  selectedState,
}: {
  eventName?: string;
  options: WorkflowStateEventOption[];
  selectedState?: WorkflowStateView | null;
}): string | null => {
  const event = eventName?.trim() ?? "";

  if (!event || !selectedState) {
    return null;
  }

  const matchedOption = options.find((option) =>
    isSameEvent(option.event, event),
  );

  if (!matchedOption) {
    return `"${event}" is a custom event. No transition in this workflow names it, so it will only be recorded in state history.`;
  }

  if (matchedOption.advances) {
    return null;
  }

  // A WAIT state is the one type where the posted event decides whether the
  // instance moves, so say what it is actually waiting for.
  if (selectedState.type === WorkflowStateType.WAIT) {
    const nextState = getWaitTransition(selectedState);

    return nextState
      ? `"${event}" will not move ${selectedState.name}. It advances only on "${nextState.event}", so this event will only be recorded in state history.`
      : `"${event}" will not move ${selectedState.name}. It has no onward transition, so this event will only be recorded in state history.`;
  }

  if (matchedOption.isGeneric) {
    return `"${event}" is one of the events that drive WAIT states, and only WAIT states advance on it. ${selectedState.name} is a ${selectedState.type.toUpperCase()} state, so it will only be recorded in state history.`;
  }

  return null;
};
